import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';

import { ReactComponent as ExpandMoreIcon } from 'applicaiton/assets/expand-more.svg';
import { getSupportedCountries } from 'applicaiton/store/reducers/Countries/ActionCreators';

import useOnClickOutside from 'common/hooks/useClickOutside';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { LocaleName } from 'common/types/locale';
import { useLanguage } from 'common/hooks/useLanguage';

import { CountryDropdownProps } from './models';
import { DropdownStyled, OptionItem, OptionsList } from './styles';

/**
 * A React component that renders a dropdown menu for selecting a country.
 *
 * @param label - The label to display above the dropdown
 * @param placeholder - The placeholder text to show when no country is selected
 * @param country - The currently selected country object, containing code and id properties
 * @param onChange - A callback function that gets called when a new country is selected,
 *  with the selected country as an argument
 * @param hint - An optional hint message to display below the dropdown
 * @param disabled - Whether or not the dropdown should be disabled
 * @param errorMessage - An optional error message to display below the dropdown if validation fails
 */
const CountryDropdown: FC<CountryDropdownProps> = ({
  label,
  placeholder,
  country,
  onChange,
  hint,
  disabled,
  errorMessage,
}) => {
  const ref = useRef(null);
  const dispatch = useAppDispatch();
  const { supportedCountries } = useAppSelector((state) => state.countries);
  const [isActive, setIsActive] = useState(false);

  const { language } = useLanguage();

  useOnClickOutside(ref, () => setIsActive(false));

  /**
   * The name of the currently selected country, based on the language code set by the
   * `useLanguage` hook. If no country is selected or if the corresponding name property
   * does not exist for the current language, an empty string is returned.
   */
  const countryName = useMemo(() => {
    return (
      supportedCountries?.find((item) => item.code === country.code)?.[
        `name_${language.toUpperCase()}` as LocaleName
      ] || ''
    );
  }, [supportedCountries, language, country]);

  /**
   * A hook that dispatches the `getSupportedCountries` action to retrieve the list of supported countries
   * from the server when the component is first mounted.
   */
  useEffect(() => {
    dispatch(getSupportedCountries());
  }, []);

  return (
    <DropdownStyled
      isActive={isActive}
      disabled={disabled}
      ref={ref}
      isError={!!errorMessage}
    >
      {label && <label htmlFor={'country-dropdown'}>{label}</label>}
      <section onMouseDown={() => !disabled && setIsActive(!isActive)}>
        <>
          {!countryName ? (
            <span>{placeholder}</span>
          ) : (
            <h2>
              <ReactCountryFlag countryCode={country.code} /> {countryName}
            </h2>
          )}
        </>
        <ExpandMoreIcon />
      </section>
      {isActive && !!supportedCountries.length && (
        <OptionsList className="country-option-list">
          {supportedCountries.map((item) => (
            <OptionItem
              key={item.id}
              onClick={() => {
                onChange({
                  code: item.code,
                  id: String(item.id),
                  name: item.name,
                });
                setIsActive(false);
              }}
              selected={country.code === String(item.code)}
            >
              <ReactCountryFlag countryCode={item.code} />
              {item[`name_${language.toUpperCase()}` as LocaleName]}
            </OptionItem>
          ))}
        </OptionsList>
      )}
      {(hint || errorMessage) && (
        <span>{errorMessage ? errorMessage : hint}</span>
      )}
    </DropdownStyled>
  );
};

export default CountryDropdown;
